import { toaster } from "@/components/ui/toaster";
import { useMerchant } from "@/hooks/useMerchant";
import useNumberFormatter from "@/hooks/useNumberFormatter";
import {
  Badge,
  Box,
  Button,
  CloseButton,
  Dialog,
  HStack,
  Heading,
  Input,
  NativeSelect,
  Portal,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { propertyUseLabels } from "../../screenings/mappers/screening.mapper";
import { useProtectTRPC } from "../../trpc";
import {
  ChangeResult,
  RetirementReason,
  changeRejectionMessages,
  retirementReasonLabels,
} from "../changes";
import { formatCalendarDate, formatMonth } from "../columns";
import { Contract } from "../types";
import { useChangeRules } from "../useChangeRules";
import { useInvalidateContracts } from "../useInvalidateContracts";
import ChangeOutcome from "./ChangeOutcome";

type Props = {
  open: boolean;
  contracts: Contract[];
  onClose: (changed: boolean) => void;
  onExitComplete: () => void;
};

type Row = { reason?: RetirementReason; note: string };

const COLUMNS = ["Inmueble", "Tipo", "Canon", "Motivo del retiro", ""];

const REASONS = Object.keys(retirementReasonLabels) as RetirementReason[];

const ReasonSelect = ({
  value,
  onChange,
  disabled,
}: {
  value?: RetirementReason;
  onChange: (reason?: RetirementReason) => void;
  disabled?: boolean;
}): JSX.Element => (
  <NativeSelect.Root size="sm" width="240px" disabled={disabled}>
    <NativeSelect.Field
      value={value ?? ""}
      onChange={(e) =>
        onChange((e.target.value || undefined) as RetirementReason | undefined)
      }
    >
      <option value="">Selecciona…</option>
      {REASONS.map((reason) => (
        <option key={reason} value={reason}>
          {retirementReasonLabels[reason]}
        </option>
      ))}
    </NativeSelect.Field>
    <NativeSelect.Indicator />
  </NativeSelect.Root>
);

const RetireModal = ({
  open,
  contracts,
  onClose,
  onExitComplete,
}: Props): JSX.Element => {
  const trpc = useProtectTRPC();
  const { merchant } = useMerchant();
  const invalidateContracts = useInvalidateContracts();
  const rules = useChangeRules();
  const formatCurrency = useNumberFormatter("es-CO", "COP", "currency");

  const [rows, setRows] = useState<Record<string, Row>>(() =>
    Object.fromEntries(contracts.map((contract) => [contract.id, { note: "" }]))
  );
  const [reasonForAll, setReasonForAll] = useState<RetirementReason>();
  const [results, setResults] = useState<ChangeResult[]>();
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => setIsMounted(true), []);

  const { mutate: retire, isPending } = useMutation({
    ...trpc.contracts.retire.mutationOptions(),
    onSuccess: (data) => {
      setResults(data.results);
      invalidateContracts();
    },
    onError: () =>
      toaster.create({
        title: "Error",
        description: "No pudimos programar los retiros. Intenta de nuevo.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      }),
  });

  const updateRow = (contractId: string, update: Partial<Row>) =>
    setRows((prev) => ({
      ...prev,
      [contractId]: { ...prev[contractId], ...update },
    }));

  const retirable = contracts.filter((contract) => !contract.hasOpenClaim);

  const items = retirable.flatMap((contract) => {
    const { reason } = rows[contract.id];
    const note = reason === "other" ? rows[contract.id].note.trim() : "";
    if (!reason || (reason === "other" && !note)) return [];

    return [{ contractId: contract.id, reason, note: note || undefined }];
  });

  const applyReasonToAll = () =>
    retirable.forEach((contract) =>
      updateRow(contract.id, { reason: reasonForAll })
    );

  const changed = !!results?.some((result) => result.outcome !== "rejected");
  const close = () => {
    if (!isPending) onClose(changed);
  };

  const handleSubmit = () => {
    if (!merchant || items.length === 0) return;

    retire({ rentalsMerchantId: merchant.id, items });
  };

  return (
    <Dialog.Root
      open={open && isMounted}
      onOpenChange={(e) => !e.open && close()}
      onExitComplete={onExitComplete}
      size="xl"
      scrollBehavior="inside"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header borderBottom="1px solid #ebecef80" padding="4">
              <Dialog.Title>
                <Heading>Retirar contratos</Heading>
                <Text textStyle="sm" color="gray.500" fontWeight="normal">
                  {contracts.length} contrato(s) · indica el motivo de cada uno
                </Text>
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton marginTop="1" disabled={isPending} />
            </Dialog.CloseTrigger>
            <Dialog.Body py={6}>
              <VStack gap={4} align="stretch">
                {!results && (
                  <Box>
                    <Text fontWeight={600} fontSize="sm" mb={2}>
                      Motivo para todos
                    </Text>
                    <HStack gap={2}>
                      <ReasonSelect
                        value={reasonForAll}
                        onChange={setReasonForAll}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!reasonForAll}
                        onClick={applyReasonToAll}
                      >
                        Aplicar a todos
                      </Button>
                    </HStack>
                  </Box>
                )}
                {rules && (
                  <Box bg="gray.50" borderRadius="md" p={3}>
                    <Text fontSize="sm" color="gray.700">
                      No todos se retiran por lo mismo: puedes poner un motivo a
                      todos y luego cambiar los que difieran. El retiro{" "}
                      <b>no es inmediato</b>: queda en trámite y el contrato
                      sale de la fianza desde{" "}
                      {formatMonth(rules.effectiveMonth)}. Puedes cancelarlo
                      desde su detalle hasta el{" "}
                      {formatCalendarDate(rules.cutoffDate)}.
                    </Text>
                  </Box>
                )}
                <Table.ScrollArea>
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row bg="#fcfcfd">
                        {COLUMNS.map((label) => (
                          <Table.ColumnHeader key={label} color="gray.500">
                            {label}
                          </Table.ColumnHeader>
                        ))}
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {contracts.map((contract) => {
                        const row = rows[contract.id];
                        const result = results?.find(
                          ({ contractId }) => contractId === contract.id
                        );

                        return (
                          <Table.Row key={contract.id}>
                            <Table.Cell>
                              <Text fontWeight={600}>
                                {contract.tenantName}
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                {contract.propertyAddress || "Sin dirección"}
                              </Text>
                            </Table.Cell>
                            <Table.Cell>
                              <Badge bg="#DFEAFF" color="#6887D8" size="md">
                                {propertyUseLabels[contract.propertyUse]}
                              </Badge>
                            </Table.Cell>
                            <Table.Cell>
                              {formatCurrency(contract.monthlyRent)}
                            </Table.Cell>
                            <Table.Cell>
                              {contract.hasOpenClaim ? (
                                <Text fontSize="sm" color="#EC5858">
                                  {changeRejectionMessages.hasOpenClaim}
                                </Text>
                              ) : (
                                <VStack align="stretch" gap={2}>
                                  <ReasonSelect
                                    value={row.reason}
                                    onChange={(reason) =>
                                      updateRow(contract.id, { reason })
                                    }
                                    disabled={!!results}
                                  />
                                  {row.reason === "other" && (
                                    <Input
                                      size="sm"
                                      width="240px"
                                      placeholder="Cuéntanos el motivo"
                                      value={row.note}
                                      maxLength={500}
                                      disabled={!!results}
                                      onChange={(e) =>
                                        updateRow(contract.id, {
                                          note: e.target.value,
                                        })
                                      }
                                    />
                                  )}
                                  {contract.pendingChanges?.retirement &&
                                    !results && (
                                      <Text fontSize="xs" color="gray.500">
                                        Ya tiene un retiro en trámite: si lo
                                        envías, lo reemplaza.
                                      </Text>
                                    )}
                                </VStack>
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              {result && (
                                <ChangeOutcome
                                  result={result}
                                  scheduledLabel={(month) =>
                                    `En trámite · sale en ${month}`
                                  }
                                />
                              )}
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table.Root>
                </Table.ScrollArea>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer borderTop="1px solid #ebecef80">
              {results ? (
                <Button onClick={close}>Cerrar</Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={close}
                    disabled={isPending}
                  >
                    Cancelar
                  </Button>
                  <Button
                    colorPalette="red"
                    onClick={handleSubmit}
                    disabled={items.length === 0}
                    loading={isPending}
                  >
                    Retirar {items.length} contrato(s)
                  </Button>
                </>
              )}
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default RetireModal;
